import { useState } from "react";
import { Bookmark, ChevronRight, MapPin, Camera } from "lucide-react";

const SIGNAL_LABELS = {
  pre_foreclosure: "Pre-foreclosure",
  code_violation: "Code violation",
  recent_eviction: "Recent eviction",
  tax_delinquent: "Tax delinquent",
  absentee_owner: "Absentee owner",
  vacant: "Vacant",
  probate: "Probate",
  high_equity: "High equity",
  long_term_owner: "Long-term owner",
};

const HEAVY = new Set(["pre_foreclosure", "code_violation", "recent_eviction", "tax_delinquent"]);

function swapViewType(url, nextType) {
  if (!url) return url;
  try {
    const u = new URL(url, window.location.origin);
    u.searchParams.set("type", nextType);
    return u.pathname + "?" + u.searchParams.toString();
  } catch {
    return url;
  }
}

export default function PropertyCard({ prop, onOpen, onSave }) {
  const [view, setView] = useState("streetview");
  const imgSrc = swapViewType(prop.image_url, view);
  const nextView = view === "streetview" ? "satellite" : "streetview";
  const toggleLabel = view === "streetview" ? "Aerial" : "Street";
  const ToggleIcon = view === "streetview" ? MapPin : Camera;

  return (
    <div
      data-testid={`property-card-${prop.id}`}
      className="surface-card p-4 flex flex-col gap-3 hover:border-[--ink] transition-colors"
    >
      {prop.image_url && (
        <div
          onClick={onOpen}
          className="relative -m-4 mb-0 aspect-[16/9] overflow-hidden bg-[--bg-soft] cursor-pointer border-b border-[--line] group"
        >
          <img
            src={imgSrc}
            alt={prop.address}
            loading="lazy"
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            onError={(e) => {
              e.currentTarget.parentElement.style.display = "none";
            }}
          />
          <button
            type="button"
            data-testid={`view-toggle-${prop.id}`}
            aria-label={`Switch to ${toggleLabel} view`}
            onClick={(e) => {
              e.stopPropagation();
              setView(nextView);
            }}
            className="absolute top-2 right-2 bg-white/90 hover:bg-white backdrop-blur-sm border border-[--line] px-2 py-1 text-[10px] font-mono font-medium uppercase tracking-wider flex items-center gap-1 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ToggleIcon size={10} strokeWidth={1.5} />
            {toggleLabel}
          </button>
          <div className="absolute top-2 left-2 bg-black/60 text-white/90 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider">
            {view === "satellite" ? "Aerial" : "Street"}
          </div>
        </div>
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-display font-bold text-base truncate">{prop.address}</div>
          <div className="text-xs text-[--ink-muted] font-mono mt-0.5">
            {prop.city}, {prop.state} · {prop.zip}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="overline text-[10px]">Motivation</div>
          <div
            data-testid={`motivation-badge-${prop.id}`}
            className={`inline-block px-2 py-0.5 font-mono font-bold text-sm leading-tight ${
              prop.motivation_score >= 75
                ? "bg-green-50 text-green-700 border border-green-200"
                : prop.motivation_score >= 55
                ? "bg-yellow-50 text-yellow-700 border border-yellow-200"
                : "bg-red-50 text-red-700 border border-red-200"
            }`}
          >
            {prop.motivation_score}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1">
        {prop.distress_signals.slice(0, 3).map((s) => (
          <span
            key={s}
            className={`text-[10px] px-1.5 py-0.5 font-medium border ${
              HEAVY.has(s)
                ? "bg-red-50 text-[--danger] border-red-200"
                : "bg-[--bg-soft] text-[--ink-soft] border-[--line]"
            }`}
          >
            {SIGNAL_LABELS[s] || s}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-2 text-xs border-t border-[--line] pt-3">
        <div>
          <div className="overline text-[10px] mb-1">ARV</div>
          <div className="font-mono font-semibold">${(prop.estimated_arv / 1000).toFixed(0)}k</div>
        </div>
        <div>
          <div className="overline text-[10px] mb-1">Repairs</div>
          <div className="font-mono">${(prop.estimated_repairs / 1000).toFixed(0)}k</div>
        </div>
        <div>
          <div className="overline text-[10px] mb-1">MAO 70%</div>
          <div className="font-mono font-semibold text-[--accent]">${(prop.mao_70 / 1000).toFixed(0)}k</div>
        </div>
        <div>
          <div className="overline text-[10px] mb-1">Asking</div>
          <div className="font-mono">${(prop.asking_price / 1000).toFixed(0)}k</div>
        </div>
        <div>
          <div className="overline text-[10px] mb-1">Equity</div>
          <div className="font-mono">{prop.owner_equity_pct}%</div>
        </div>
        <div>
          <div className="overline text-[10px] mb-1">Profit</div>
          <div className="font-mono font-semibold text-[--success]">
            ${(prop.potential_profit / 1000).toFixed(0)}k
          </div>
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <button
          data-testid={`save-deal-${prop.id}`}
          onClick={onSave}
          className="btn-ghost !py-1.5 !px-3 flex items-center gap-1.5 text-xs"
        >
          <Bookmark size={12} strokeWidth={1.5} /> Save
        </button>
        <button
          data-testid={`open-deal-${prop.id}`}
          onClick={onOpen}
          className="btn-primary !py-1.5 !px-3 flex items-center gap-1.5 text-xs ml-auto"
        >
          Detail <ChevronRight size={12} strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}
